import { apiCall } from 'modules/core/services/api'
import { safeGetLocalStorage, safeSetLocalStorage } from 'modules/core/services/safe-local-storage'
import {
  buildScheduledInstant,
  DEFAULT_TIMEZONE,
  getBrowserTimezone,
} from 'modules/common/utils/business-timezone'
import { getScheduleTimeForDate } from 'modules/common/utils/schedule-time-options'
import { useChatStatusStore } from 'modules/creative/store/chat-status-store'
import { useBrandKitStore } from 'modules/creative/store/brand-kit-store'
import { cancelCreativePersist, useCreativeStore } from 'modules/creative/store/creative-store'
import { ApiError } from 'openapi'
import { OpenAPI } from 'openapi/core/OpenAPI'
import { request as __request } from 'openapi/core/request'
import { CreativeService } from 'openapi/services/CreativeService'
import { create } from 'zustand'
import type { CalendarEntry, CalendarView } from '../types'

function fetchCalendarEntries(from: string, to: string): Promise<CalendarEntry[]> {
  return __request(OpenAPI, {
    method: 'GET',
    url: '/v1/calendar/entries',
    query: { from, to },
  })
}

function fetchUpcomingEntries(): Promise<CalendarEntry[]> {
  return __request(OpenAPI, {
    method: 'GET',
    url: '/v1/calendar/upcoming',
  })
}

function scheduleCreativeApi(creativeId: string, scheduledAt: string): Promise<CalendarEntry> {
  return __request(OpenAPI, {
    method: 'PATCH',
    url: '/v1/calendar/entries/{creativeId}/schedule',
    path: { creativeId },
    body: { scheduledAt },
  })
}

function unscheduleCreativeApi(creativeId: string): Promise<void> {
  return __request(OpenAPI, {
    method: 'PATCH',
    url: '/v1/calendar/entries/{creativeId}/unschedule',
    path: { creativeId },
  })
}

// Approval is project-level — this stamps approvedAt on the post's parent
// project so it publishes at its scheduled time. Same endpoint the weekly
// cockpit uses; the calendar collapses a post's siblings into one entry, so a
// single approve covers every platform the post targets.
function approveCreativeApi(creativeId: string): Promise<unknown> {
  return __request(OpenAPI, {
    method: 'POST',
    url: '/v1/creatives/{id}/approve',
    path: { id: creativeId },
  })
}

interface CalendarState {
  entries: CalendarEntry[]
  loading: boolean
  currentDate: Date
  view: CalendarView
  selectedEntry: CalendarEntry | null
  upcomingEntries: CalendarEntry[]
  // Creative ids the user deleted locally. Stripped from every fetch
  // response so an in-flight GET that started before the DELETE completed
  // on the server can't resurrect the entry. Session-scoped tombstones; the
  // server is the source of truth after a refresh.
  suppressedCreativeIds: Set<string>

  fetchEntries: (from: string, to: string) => Promise<void>
  setCurrentDate: (date: Date) => void
  setView: (view: CalendarView) => void
  selectEntry: (entry: CalendarEntry | null) => void

  scheduleCreative: (creativeId: string, scheduledAt: string) => Promise<void>
  unscheduleCreative: (creativeId: string) => Promise<void>
  /**
   * Approve a review-mode post so it publishes at its scheduled time. Stamps
   * approvedAt on the parent project (project-level, post-wide). Optimistically
   * clears `needsApproval` on the matching entry so the Approve CTA disappears
   * immediately; reverts and re-throws if the server call fails.
   */
  approveCreative: (creativeId: string) => Promise<void>
  rescheduleEntry: (entryId: string, newDate: string) => Promise<void>
  fetchUpcoming: () => Promise<void>
  /**
   * Delete a creative AND dismiss its calendar cell. Returns after the server
   * delete completes, but the local state is cleared optimistically so the
   * cell goes empty immediately. If the server delete fails, the optimistic
   * state is reverted and the error is re-thrown so callers can show a toast.
   */
  deleteAndDismissCreative: (creativeId: string) => Promise<void>
}

function filterSuppressed(
  entries: CalendarEntry[] | undefined,
  suppressedCreativeIds: Set<string>
): CalendarEntry[] {
  if (!entries) return []
  if (suppressedCreativeIds.size === 0) return entries
  return entries.filter(e => !(e.creativeId && suppressedCreativeIds.has(e.creativeId)))
}

// Monotonic token so concurrent fetchEntries calls can coalesce: only the
// response for the most recently issued token is committed to state.
// Earlier in-flight responses are dropped when a newer request has since
// been issued, which prevents stale data (e.g. an older date range or a
// response issued before a Pusher-driven refetch) from clobbering the UI.
let latestEntriesFetchToken = 0

/** Tombstoned creative ids are evicted after TTL so long-lived tabs do not grow the set forever. */
const SUPPRESSED_CREATIVE_ID_TTL_MS = 5 * 60 * 1000
const suppressedCreativeEvictionTimers = new Map<string, ReturnType<typeof setTimeout>>()

function scheduleSuppressedCreativeIdEviction(creativeId: string) {
  const prev = suppressedCreativeEvictionTimers.get(creativeId)
  if (prev) clearTimeout(prev)
  const t = setTimeout(() => {
    suppressedCreativeEvictionTimers.delete(creativeId)
    useCalendarStore.setState(s => {
      if (!s.suppressedCreativeIds.has(creativeId)) return {}
      const suppressedCreativeIds = new Set(s.suppressedCreativeIds)
      suppressedCreativeIds.delete(creativeId)
      return { suppressedCreativeIds }
    })
  }, SUPPRESSED_CREATIVE_ID_TTL_MS)
  suppressedCreativeEvictionTimers.set(creativeId, t)
}

export const useCalendarStore = create<CalendarState>((set, get) => ({
  entries: [],
  loading: false,
  currentDate: new Date(),
  view: (safeGetLocalStorage('calendar_view') as CalendarView) || 'week',
  selectedEntry: null,
  upcomingEntries: [],
  suppressedCreativeIds: new Set(),

  fetchEntries: async (from: string, to: string) => {
    // Assign this call a monotonic token. If another fetchEntries is issued
    // before this one resolves, the later token wins and we discard our
    // result. Keeps `loading` visible for the UI but never silently drops
    // requests the way the old `if (loading) return` guard did.
    const token = ++latestEntriesFetchToken
    set({ loading: true })
    try {
      const entries = await apiCall(fetchCalendarEntries, null, from, to)
      if (token !== latestEntriesFetchToken) return
      const { suppressedCreativeIds } = get()
      set({
        entries: filterSuppressed(entries, suppressedCreativeIds),
        loading: false,
      })
    } catch (err) {
      if (token !== latestEntriesFetchToken) return
      // Preserve previously shown entries on transient failures (network
      // blip, 5xx) instead of wiping the calendar. The prior behavior of
      // setting `entries: []` made every blip look like a signed-out or
      // empty-calendar state, which is more alarming than useful. Pusher
      // completions and user navigation will retry naturally.
      console.error('fetchCalendarEntries failed; keeping previous entries:', err)
      set({ loading: false })
    }
  },

  setCurrentDate: (date: Date) => set({ currentDate: date }),
  setView: (view: CalendarView) => {
    safeSetLocalStorage('calendar_view', view)
    set({ view })
  },
  selectEntry: (entry: CalendarEntry | null) => set({ selectedEntry: entry }),

  scheduleCreative: async (creativeId: string, scheduledAt: string) => {
    const updated = await apiCall(scheduleCreativeApi, null, creativeId, scheduledAt)
    // Add or update entry in the list
    set(state => {
      const filtered = state.entries.filter(e => e.creativeId !== creativeId)
      return { entries: [...filtered, updated].sort((a, b) => a.date.localeCompare(b.date)) }
    })
  },

  unscheduleCreative: async (creativeId: string) => {
    await apiCall(unscheduleCreativeApi, null, creativeId)
    set(state => ({
      entries: state.entries.filter(e => e.creativeId !== creativeId),
      selectedEntry: state.selectedEntry?.creativeId === creativeId ? null : state.selectedEntry,
    }))
  },

  approveCreative: async (creativeId: string) => {
    // Snapshot for revert, then optimistically drop the Approve CTA on the
    // matching entry (in both the grid and the upcoming widget) so the card
    // reads as approved/scheduled the instant the user clicks.
    const { entries: previousEntries, upcomingEntries: previousUpcoming } = get()
    const clearNeedsApproval = (e: CalendarEntry): CalendarEntry =>
      e.creativeId === creativeId ? { ...e, needsApproval: false } : e
    set(state => ({
      entries: state.entries.map(clearNeedsApproval),
      upcomingEntries: state.upcomingEntries.map(clearNeedsApproval),
    }))
    try {
      await apiCall(approveCreativeApi, null, creativeId)
    } catch (err) {
      set({ entries: previousEntries, upcomingEntries: previousUpcoming })
      throw err
    }
  },

  rescheduleEntry: async (entryId: string, newDate: string) => {
    const entry = get().entries.find(e => e.id === entryId)
    if (!entry?.creativeId) return

    // Optimistic update: move the entry to the new date.
    set(state => {
      const entries = state.entries.map(e => (e.id === entryId ? { ...e, date: newDate } : e))
      return { entries: entries.sort((a, b) => a.date.localeCompare(b.date)) }
    })

    try {
      // Reschedule lands at 10am wall-clock in the brand's timezone (browser
      // TZ as a transient fallback before brand-kit hydration), matching the
      // ScheduleDialog default. Persisting `${date}T10:00:00Z` here would be
      // off by the user's UTC offset. When the drop target is today and 10am
      // has passed, fall back to the first slot still available so we never
      // reschedule into the past.
      const timezone =
        useBrandKitStore.getState().brandKit.timezone ?? getBrowserTimezone() ?? DEFAULT_TIMEZONE
      const updated = await apiCall(
        scheduleCreativeApi,
        null,
        entry.creativeId,
        buildScheduledInstant(newDate, getScheduleTimeForDate(newDate, timezone), timezone)
      )
      // Reconcile with server response
      set(state => ({
        entries: state.entries
          .map(e => (e.creativeId === entry.creativeId ? { ...e, ...updated } : e))
          .sort((a, b) => a.date.localeCompare(b.date)),
      }))
    } catch {
      // Revert on failure — refetch
      const state = get()
      const from = state.entries[0]?.date
      const to = state.entries[state.entries.length - 1]?.date
      if (from && to) get().fetchEntries(from, to)
    }
  },

  fetchUpcoming: async () => {
    try {
      const upcomingEntries = await apiCall(fetchUpcomingEntries, null)
      const { suppressedCreativeIds } = get()
      set({
        upcomingEntries: filterSuppressed(upcomingEntries ?? [], suppressedCreativeIds),
      })
    } catch (err) {
      // Preserve whatever we already had; a blip shouldn't blank the
      // upcoming-entries widget. See fetchEntries above for rationale.
      console.error('fetchUpcomingEntries failed; keeping previous upcoming:', err)
    }
  },

  deleteAndDismissCreative: async (creativeId: string) => {
    const state = get()
    const entry =
      state.entries.find(e => e.creativeId === creativeId) ??
      state.upcomingEntries.find(e => e.creativeId === creativeId)

    // Snapshot enough state to revert if the server delete fails.
    const previousEntries = state.entries
    const previousUpcoming = state.upcomingEntries
    const previousSelected = state.selectedEntry
    const hadSuppressedId = state.suppressedCreativeIds.has(creativeId)

    // Optimistically drop the deleted creative so the cell clears immediately.
    // The creativeId is tombstoned in `suppressedCreativeIds` so any
    // fetchEntries response that was in flight when the user hit delete can't
    // restore the entry before the server DELETE lands.
    set(s => {
      const suppressedCreativeIds = new Set(s.suppressedCreativeIds)
      suppressedCreativeIds.add(creativeId)

      return {
        entries: s.entries.filter(e => e.creativeId !== creativeId),
        upcomingEntries: s.upcomingEntries.filter(e => e.creativeId !== creativeId),
        selectedEntry: s.selectedEntry?.creativeId === creativeId ? null : s.selectedEntry,
        suppressedCreativeIds,
      }
    })

    scheduleSuppressedCreativeIdEviction(creativeId)

    // The server routes the creative delete through its parent Project delete,
    // so the FK cascade removes every platform sibling. Mirror that locally by
    // clearing all of the project's creatives (not just the representative one
    // backing this entry), so the composer/posts surfaces don't keep ghosts of
    // siblings the server just cascaded away. Falls back to the single id when
    // the entry has no projectId (legacy / standalone).
    const projectId = entry?.projectId ?? null
    const clearComposerStateForDeletedCreative = () => {
      const removedIds = new Set<string>([creativeId])
      if (projectId) {
        for (const c of useCreativeStore.getState().creatives) {
          if (c.projectId === projectId) removedIds.add(c.id)
        }
      }
      useCreativeStore.setState(s => ({
        creatives: s.creatives.filter(c => !removedIds.has(c.id)),
      }))
      for (const removedId of removedIds) {
        useChatStatusStore.getState().clearCreative(removedId)
      }
    }

    try {
      cancelCreativePersist(creativeId)
      await apiCall(CreativeService.creativeControllerDeleteV1, null, creativeId)
      // Mirror the creative-store's local cleanup without going through its
      // own swallow-all-errors delete path. Clearing chat status prevents a
      // ghost "generating" badge in the composer if the user navigates to
      // this (now-deleted) creative from another surface before refresh.
      clearComposerStateForDeletedCreative()
    } catch (err) {
      // Parallel double-delete: second request 404s after the row is gone.
      if (err instanceof ApiError && err.status === 404) {
        clearComposerStateForDeletedCreative()
        return
      }
      // Server rejected the delete — restore everything we just cleared so
      // the UI matches reality, then bubble up so the caller can toast.
      set(s => {
        const suppressedCreativeIds = new Set(s.suppressedCreativeIds)
        if (!hadSuppressedId) suppressedCreativeIds.delete(creativeId)
        return {
          entries: previousEntries,
          upcomingEntries: previousUpcoming,
          selectedEntry: previousSelected,
          suppressedCreativeIds,
        }
      })
      throw err
    }
  },
}))
